import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AbstractControl } from '@angular/forms';
import { INimbusFormGroup } from '@nimbus-library/features/shared/angular/forms/i-nimbus-form-group';
import { isFormValueEqual } from '@nimbus-library/features/shared/angular/forms';

export class NimbusFormGroupWrapper<TForm = unknown> {
  private originalFormValue: TForm;
  private readonly subscriptions$: Subscription = new Subscription();
  private readonly dataChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public get originalValue(): TForm {
    return this.originalFormValue;
  }

  public get hasChange(): boolean {
    return this.dataChanged$.value;
  }

  public get hasChange$(): Observable<boolean> {
    return this.dataChanged$.asObservable();
  }

  public constructor(public readonly form: INimbusFormGroup<TForm>) {
    this.originalFormValue = this.form.getRawValue();
    this.subscriptions$.add(
      this.form.valueChanges.subscribe({
        next: () => this.updateChangeState(),
      }),
    );
  }

  public reset(value: TForm): void {
    this.originalFormValue = value;
    // Re-evaluate against the new baseline immediately. Without this, a `dataChanged$=true`
    // emitted by an earlier valueChanges stays stuck (no form mutation follows the reset),
    // leaving consumers like `BaseEditableManage.hasChange` permanently true even though the
    // form value matches the just-installed baseline.
    this.dataChanged$.next(!isFormValueEqual(this.form.getRawValue(), value));
  }

  public destroy(): void {
    this.dataChanged$.complete();
    this.subscriptions$.unsubscribe();
  }

  public markFormPristine(): void {
    let control: AbstractControl = null;
    this.form.markAsUntouched();
    Object.keys(this.form.controls).forEach((name) => {
      control = this.form.controls[name];
      control.markAsPristine();
      control.setErrors(null);
    });
  }

  private updateChangeState(): void {
    if (this.form.pristine) {
      this.originalFormValue = this.form.getRawValue();
    }
    const dataChanged = !isFormValueEqual(this.form.getRawValue(), this.originalFormValue);
    this.dataChanged$.next(dataChanged);
  }
}
